import classNames from 'classnames/bind'
import { useCallback, useMemo } from 'react'
import { Navigate } from 'react-router-dom'

import { ReactComponent as DropdownSVG } from '@percent/cause-dashboard/common/assets/images/dropdown.svg'
import {
  useCausesPortalAnalytics,
  useAuthDispatch,
  useAuthState,
  useNotificationBar,
  useSitewideMessage,
  useTranslatedRole
} from '@percent/cause-dashboard/common/hooks'
import { SET_LOGOUT, selectAuthState } from '@percent/cause-dashboard/context/auth'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { Avatar, Breadcrumbs, Flex, Icon, Menu, SectionItem, SectionProps, Typography } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import styles from './TopBar.module.scss'
import { useBreadcrumbs } from '../breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { RoutePath } from '../../routes/Routes'

const cx = classNames.bind(styles)

export interface TopBarProps {
  mobileMenuOpen?: boolean
  onOpenMobileMenu?: () => void
}

export function TopBar({ mobileMenuOpen, onOpenMobileMenu }: TopBarProps = {}) {
  const { t } = useTranslation()
  const { translateRole } = useTranslatedRole()
  const { authDispatch } = useAuthDispatch()
  const { authService } = useServices()
  const {
    authState: appState,
    authState: { organisation, user }
  } = useAuthState()
  const { claimAccepted, rejectedBankDetails } = selectAuthState(appState)
  const { reset: resetAnalytics } = useCausesPortalAnalytics()
  const { breadcrumb } = useBreadcrumbs()

  const { isOpened: isNotificationBarOpened } = useNotificationBar()

  const handleLogout = useCallback(() => {
    authService.logout().then(() => {
      resetAnalytics()
      authDispatch({
        type: SET_LOGOUT
      })
    })

    return <Navigate to="/" />
  }, [resetAnalytics, authDispatch, authService])

  const menuSections = useMemo<SectionProps[]>(() => {
    const sectionItems: SectionItem[] = [
      {
        label: t('menu.myAccount'),
        key: 'myAccount',
        as: 'link',
        to: RoutePath.MY_ACCOUNT,
        icon: 'user-circle'
      }
    ]

    if (user?.organisationId)
      sectionItems.push({
        label: t('menu.myTeam'),
        key: 'myTeam',
        as: 'link',
        to: RoutePath.USERS,
        icon: 'users'
      })

    sectionItems.push({
      label: t('menu.logout'),
      key: 'logOut',
      as: 'button',
      onClick: handleLogout,
      icon: 'log-out',
      dataTestId: 'navigation-bar-logout-button'
    })
    return [{ items: sectionItems }]
  }, [handleLogout, t, user?.organisationId])

  const notification = useNotificationBar()
  const { shouldShowSitewideMessage } = useSitewideMessage()
  const shiftDownToAccomodateNotification = notification.isOpened || shouldShowSitewideMessage ? 48 : 0

  return (
    <div className={styles.wrapper} style={{ top: shiftDownToAccomodateNotification }}>
      <Flex
        align="center"
        px={24}
        justify="space-between"
        w="100%"
        gap={16}
        className={cx({
          verifiedNotification: claimAccepted,
          unverifiedNotification: !claimAccepted,
          rejectedBankDetails: rejectedBankDetails,
          notificationBarOpened: isNotificationBarOpened,
          notificationBarClosed: !isNotificationBarOpened
        })}
      >
        <Flex align="center" gap={12} className={styles.leftSection}>
          <div className={styles.mobileMenuButton}>
            <button
              type="button"
              className={styles.hamburgerButton}
              onClick={() => onOpenMobileMenu?.()}
              aria-label={t('menu.openNavigationMenu')}
              aria-expanded={!!mobileMenuOpen}
              aria-controls="cause-dashboard-side-nav"
              data-testid="mobile-menu-open"
            >
              <Icon name="menu" size={6} color="black" />
            </button>
          </div>
          <div className={styles.breadcrumbsWrap}>
            <Breadcrumbs items={breadcrumb} aria-label="breadcrumb" />
          </div>
        </Flex>
        <Flex direction="row-reverse" className={styles.rightSection}>
          <div>
            <Menu
              sections={menuSections}
              customTriggerComponent={
                <div className={styles.dropdown} data-testid="topbar-menu-trigger">
                  <Menu.Item className={styles.menuItem}>
                    {organisation?.claimedAt && <Avatar image={organisation?.logo} alt={organisation?.name || ''} />}
                    <div className={styles.userDetail} data-testid="navigation-bar-user-details">
                      <Typography className={styles.nameText}>
                        {user?.fullName ?? user?.preferredName ?? user?.email}
                      </Typography>
                      <Typography className={styles.roleText} variant="small">
                        <span data-testid={'user-role'}>{user?.role ? translateRole(user.role).displayName : ''}</span>
                      </Typography>
                    </div>
                    <DropdownSVG className={styles.icon} />
                  </Menu.Item>
                </div>
              }
            />
          </div>
        </Flex>
      </Flex>
    </div>
  )
}
